/** @jsxImportSource ../../i18n */
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '../../theme/cx';

const moveFocus = (event, refs, index, onSelect) => {
  const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  let next = null;
  if (event.key in keys) next = (index + keys[event.key] + refs.length) % refs.length;
  if (event.key === 'Home') next = 0;
  if (event.key === 'End') next = refs.length - 1;
  if (next === null) return;
  event.preventDefault();
  refs[next]?.focus();
  onSelect(next);
};

// Keeps the active tab visible when the tab list scrolls horizontally
// (narrow screens), without scrolling the page.
const revealActive = (list, tab) => {
  if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
  const box = list.getBoundingClientRect();
  const r = tab.getBoundingClientRect();
  if (r.left < box.left) list.scrollLeft -= box.left - r.left + 16;
  else if (r.right > box.right) list.scrollLeft += r.right - box.right + 16;
};

const Count = ({ value, active }) => (
  <span className={cx('num ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold', active ? 'bg-gold-500/15 text-gold-300' : 'bg-surface-3 text-fg-subtle')}>
    {value}
  </span>
);

export const Tabs = ({ items, value, onChange, variant = 'underline', ariaLabel, className }) => {
  const refs = useRef([]);
  const listRef = useRef(null);
  const select = (index) => onChange?.(items[index].id);
  const activeIndex = items.findIndex((item) => item.id === value);
  useEffect(() => { revealActive(listRef.current, refs.current[activeIndex]); }, [activeIndex]);
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cx(
        variant === 'pill'
          ? 'inline-flex max-w-full gap-1 overflow-x-auto rounded-control border border-line bg-surface-1 p-1'
          : 'flex max-w-full gap-6 overflow-x-auto shadow-[inset_0_-1px_0_rgb(var(--c-line))]',
        className,
      )}
    >
      {items.map((item, index) => {
        const active = item.id === value;
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            ref={(el) => { refs.current[index] = el; }}
            type="button"
            role="tab"
            id={item.tabId}
            aria-selected={active}
            aria-controls={item.panelId}
            tabIndex={active ? 0 : -1}
            onClick={() => select(index)}
            onKeyDown={(e) => moveFocus(e, refs.current, index, select)}
            className={cx(
              'inline-flex flex-shrink-0 items-center whitespace-nowrap text-sm font-medium transition-colors duration-150',
              variant === 'pill'
                ? cx('h-9 rounded-[8px] px-4', active ? 'bg-surface-3 text-fg shadow-[inset_0_0_0_1px_rgb(var(--c-line-strong))]' : 'text-fg-muted hover:text-fg')
                : cx('relative h-11 border-b-2', active ? 'border-gold-500 text-fg' : 'border-transparent text-fg-muted hover:text-fg'),
            )}
          >
            {Icon && <Icon size={16} className={cx('mr-2', active ? 'text-gold-400' : 'text-fg-subtle')} aria-hidden="true" />}
            {item.label}
            {item.count !== undefined && item.count !== null && <Count value={item.count} active={active} />}
          </button>
        );
      })}
    </div>
  );
};

export const NavTabs = ({ items, current, ariaLabel, className }) => (
  <nav aria-label={ariaLabel} className={cx('flex max-w-full gap-6 overflow-x-auto shadow-[inset_0_-1px_0_rgb(var(--c-line))]', className)}>
    {items.map((item) => {
      const active = item.id === current;
      return (
        <Link
          key={item.id}
          to={item.to}
          aria-current={active ? 'page' : undefined}
          className={cx(
            'relative inline-flex h-11 flex-shrink-0 items-center whitespace-nowrap border-b-2 text-sm font-medium transition-colors duration-150',
            active ? 'border-gold-500 text-fg' : 'border-transparent text-fg-muted hover:text-fg',
          )}
        >
          {item.label}
        </Link>
      );
    })}
  </nav>
);
